// SPDX-License-Identifier: Apache-2.0
import { sanitizeGeometryType, XmlTypes } from './xmlTypes';

export interface LayerAttribute<WfsXmlTypes = XmlTypes> {
  name: string;
  type: WfsXmlTypes;
  valueList?: string[];
}

class ServerWfs<WfsXmlTypes = XmlTypes> {
  public name: string;
  public url: string;
  public layers: Record<string, LayerAttribute<WfsXmlTypes>[]> = {};
  public featureTypeToGeometryColumnName: { [key: string]: LayerAttribute<WfsXmlTypes> } = {};
  public initialized: boolean;

  public constructor(name: string, url: string) {
    this.name = name;
    this.url = url;
    this.initialized = false;
  }

  public addLayer(layer: string) {
    if (!this.layers[layer]) {
      this.layers[layer] = [];
    }
  }

  public removeLayer(layer: string) {
    delete this.layers[layer];
    delete this.featureTypeToGeometryColumnName[layer];
  }

  public addLayerAttribute(layer: string, name: string, type: string) {
    this.addLayer(layer);
    this.layers[layer].push({
      name: name,
      type: type as WfsXmlTypes
    });
  }

  public addLayerGeometryAttribute(layer: string, name: string, type: string) {
    this.addLayer(layer);
    const geomType = sanitizeGeometryType(type);
    if (geomType === 'unknown') {
      console.warn(`Unknown geometry type ${type} for layer ${layer}`);
    }
    this.featureTypeToGeometryColumnName[layer] = { name: name, type: geomType as WfsXmlTypes };
  }

  public getGeometryColumnNameToFeatureTypes(featureTypes: string[]): Record<string, string[]> {
    if (!this.initialized) {
      throw new Error('Initialize WFS server before trying to get geometry column names.');
    }
    const geometryColumnNameToFeatureType: Record<string, string[]> = {};

    for (const featureType of featureTypes) {
      if (!(featureType in this.layers)) {
        console.warn(
          `featureType ${featureType} is unknown to WFS server ${this.url}\navailable featureTypes: ${Object.keys(this.layers).join(',')}`
        );
      }
      const geometryColumnName = this.featureTypeToGeometryColumnName[featureType]?.name;
      if (!(geometryColumnName in geometryColumnNameToFeatureType)) {
        geometryColumnNameToFeatureType[geometryColumnName] = [];
      }
      geometryColumnNameToFeatureType[geometryColumnName].push(featureType);
    }

    return geometryColumnNameToFeatureType;
  }
}

export default ServerWfs;
